import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminUserModel,
  LeadModel,
  Project,
  ProjectDetails,
  UserModel,
  WaitlistModel,
  MenuItemModel,
  Customer,
} from "../types";
import { InsuranceCompany } from "../types/InsuranceCompany";
import { Material } from "../types/Material";
import { Estimate, EstimateAddOns } from "../types/Estimate";
import {
  ContractorChangeOrders,
  ContractorReportTypes,
  Contractors,
  LaborItems,
  LaborMeasurements,
  MaterialCategories,
  MaterialItems,
  MaterialMeasurements,
  MiscFields,
  ProductSystem,
  Tag,
} from "../types/creation";
import { CustomerProjectDetails } from "../types/projectDetails";
import { RolePermission } from "../../types/interface";

export enum Sections {
  ANALYTICS_DASHBOARD = "analytics_dashboard",
  LEADS = "leads",
  WAITLIST = "waitlist",
  PROJECTS = "projects",
  PROJECT_ASSIGNMENT = "project_assignment",
  CUSTOMERS = "customers",
  MANAGE_WORKFLOW = "manage_workflow",
  INSURANCE_PROVIDER = "insurance_provider",
  USERS = "users",
  PRODUCT_SYSTEM = "product_system",
  SALES_ESTIMATE_CALCULATOR = "sales_estimate_calculator",
  NOTIFICATIONS = "notifications",
  FILE_MANAGER = "file_manager",
}

export type PermissionType = "READ" | "WRITE";

export interface UserState {
  permissions: { name: string; permission: PermissionType[]; subSections: RolePermission[]; }[];
  userProfile: UserModel;
  adminUsers: AdminUserModel[];
  leads: LeadModel[];
  waitlist: WaitlistModel[];
  projects: Project[];
  insuranceCompanies: InsuranceCompany[];
  projectDetails: ProjectDetails;
  materials: Material[];
  estimate: Estimate;
  estimateAddOns: EstimateAddOns[];

  productSystems: ProductSystem[];
  miscFields: MiscFields[];
  materialCategories: MaterialCategories[];
  materialMeasurements: MaterialMeasurements[];
  materialItems: MaterialItems[];
  laborMeasurements: LaborMeasurements[];
  contractors: Contractors[];
  laborItems: LaborItems[];
  tags: Tag[];
  contractorReportTypes: ContractorReportTypes[];
  contractorChangeOrders: ContractorChangeOrders[];
  drawerMenuItem: MenuItemModel;
  customerDetails: Customer;
  customerProjectDetails: CustomerProjectDetails;
}

const initialState: UserState = {
  permissions: [],
  userProfile: {} as UserModel,
  adminUsers: [] as AdminUserModel[],
  leads: [] as LeadModel[],
  projects: [] as Project[],
  waitlist: [] as WaitlistModel[],
  insuranceCompanies: [] as InsuranceCompany[],
  projectDetails: {} as ProjectDetails,
  materials: [] as Material[],
  estimate: {} as Estimate,
  estimateAddOns: [] as EstimateAddOns[],

  productSystems: [] as ProductSystem[],
  miscFields: [] as MiscFields[],
  materialCategories: [] as MaterialCategories[],
  materialMeasurements: [] as MaterialMeasurements[],
  materialItems: [] as MaterialItems[],
  laborMeasurements: [] as LaborMeasurements[],
  contractors: [] as Contractors[],
  laborItems: [] as LaborItems[],
  tags: [] as Tag[],
  contractorReportTypes: [] as ContractorReportTypes[],
  contractorChangeOrders: [] as ContractorChangeOrders[],

  drawerMenuItem: {
    activeMenuItem: "",
    isSettingActive: false,
  } as MenuItemModel,
  customerDetails: {} as Customer,

  customerProjectDetails: {} as CustomerProjectDetails,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    userLogin(state, action: PayloadAction<UserModel>) {
      const profileData = action.payload;
      state.userProfile = profileData;
    },
    setPermissions(state, action: PayloadAction<{ name: string; permission: PermissionType[]; subSections: RolePermission[]; }[]>) {
      state.permissions = action.payload;
    },
    userLogout(state) {
      state.userProfile = {} as UserModel;
    },
    setLeads(state, action: PayloadAction<LeadModel[]>) {
      state.leads = action.payload;
    },
    setWaitlist(state, action: PayloadAction<WaitlistModel[]>) {
      state.waitlist = action.payload;
    },
    setProjects(state, action: PayloadAction<Project[]>) {
      state.projects = action.payload;
    },
    setInsuranceCompanies(state, action: PayloadAction<InsuranceCompany[]>) {
      state.insuranceCompanies = action.payload;
    },
    setProjectDetails(state, action: PayloadAction<ProjectDetails>) {
      state.projectDetails = action.payload;
    },
    setMaterials(state, action: PayloadAction<Material[]>) {
      state.materials = action.payload;
    },
    setEstimate(state, action: PayloadAction<Estimate>) {
      state.estimate = action.payload;
    },
    setEstimateAddOns(state, action: PayloadAction<EstimateAddOns[]>) {
      state.estimateAddOns = action.payload;
    },
    setAdminUsers(state, action: PayloadAction<AdminUserModel[]>) {
      state.adminUsers = action.payload;
    },

    setProductSystems(state, action: PayloadAction<ProductSystem[]>) {
      state.productSystems = action.payload;
    },
    setMiscFields(state, action: PayloadAction<MiscFields[]>) {
      state.miscFields = action.payload;
    },
    setMaterialCategories(state, action: PayloadAction<MaterialCategories[]>) {
      state.materialCategories = action.payload;
    },
    setMaterialMeasurements(
      state,
      action: PayloadAction<MaterialMeasurements[]>
    ) {
      state.materialMeasurements = action.payload;
    },
    setMaterialItems(state, action: PayloadAction<MaterialItems[]>) {
      state.materialItems = action.payload;
    },
    setLaborMeasurements(state, action: PayloadAction<LaborMeasurements[]>) {
      state.laborMeasurements = action.payload;
    },
    setContractors(state, action: PayloadAction<Contractors[]>) {
      state.contractors = action.payload;
    },
    setLaborItems(state, action: PayloadAction<LaborItems[]>) {
      state.laborItems = action.payload;
    },
    setTags(state, action: PayloadAction<Tag[]>) {
      state.tags = action.payload;
    },
    setContractorReportTypes(
      state,
      action: PayloadAction<ContractorReportTypes[]>
    ) {
      state.contractorReportTypes = action.payload;
    },
    setContractorChangeOrders(
      state,
      action: PayloadAction<ContractorChangeOrders[]>
    ) {
      state.contractorChangeOrders = action.payload;
    },

    setDrawerActiveMenuItem(state, action: PayloadAction<MenuItemModel>) {
      state.drawerMenuItem.activeMenuItem = action.payload.activeMenuItem;
    },
    setDrawerIsSettingActive(state, action: PayloadAction<MenuItemModel>) {
      state.drawerMenuItem.isSettingActive = action.payload.isSettingActive;
    },
    setCustomerDetails(state, action: PayloadAction<Customer>) {
      state.customerDetails = action.payload;
    },
    setCustomerProjectDetails(
      state,
      action: PayloadAction<CustomerProjectDetails>
    ) {
      state.customerProjectDetails = action.payload;
    },
  },
});
export const {
  userLogin,
  setPermissions,
  setLeads,
  setWaitlist,
  setProjects,
  setInsuranceCompanies,
  setProjectDetails,
  setMaterials,
  setEstimate,
  setEstimateAddOns,
  userLogout,
  setAdminUsers,
  setCustomerDetails,
  setCustomerProjectDetails,

  setProductSystems,
  setMiscFields,
  setMaterialCategories,
  setMaterialMeasurements,
  setMaterialItems,
  setLaborItems,
  setTags,
  setLaborMeasurements,
  setContractorChangeOrders,
  setContractorReportTypes,
  setContractors,

  setDrawerActiveMenuItem,
  setDrawerIsSettingActive,
} = userSlice.actions;
export default userSlice.reducer;
